import { CalendarDays, Check, X } from 'lucide-react'
import { ApiError, Button } from '../../core/index.js'
import { useRespondToBridgeCallout, useWorkerBridgeCallouts } from '../hooks.js'
import { calloutIdOfMessage, describeResult } from './bridgeCardData.js'

/** What the shell knows about a message: its kind and the key naming the callout; structural, so this module imports nothing of the messages module's. */
export interface BridgeMessage {
  type: string
  dedupeKey?: string | null
}

interface BridgeMessageActionsProps {
  workerId: string
  message: BridgeMessage
  /** Opens the Shifts tab, where an accepted shift sits on the calendar. */
  onOpenShifts?: () => void
}

/**
 * The buttons a Clinical Bridge message carries on its card (plan section
 * 7, the demo's result cards): the alert that a shift is open answers
 * right there with I'll take it and Can't make it, and once it is settled
 * says what became of it; the confirmation leads to the Shifts calendar.
 * Renders nothing for a message about anything else.
 */
export function BridgeMessageActions({ workerId, message, onOpenShifts }: BridgeMessageActionsProps) {
  const calloutId = calloutIdOfMessage(message)
  const callouts = useWorkerBridgeCallouts(message.type === 'bridge_callout' && calloutId !== null ? workerId : null)
  const respond = useRespondToBridgeCallout(workerId)
  if (message.type === 'bridge_filled') {
    return onOpenShifts ? (
      <Button icon={CalendarDays} variant="secondary" size="sm" onClick={onOpenShifts}>
        See it on Shifts
      </Button>
    ) : null
  }
  if (message.type !== 'bridge_callout' || calloutId === null || !callouts.isSuccess) {
    return null
  }
  const callout = callouts.data.find((entry) => entry.calloutId === calloutId)
  if (!callout) {
    return null
  }
  if (callout.result !== 'open') {
    return <span className="text-[11px] text-ink-mid">{describeResult(callout)}</span>
  }
  const failure = respond.isError ? (respond.error instanceof ApiError ? respond.error.message : 'Your answer could not be saved. Try again.') : null
  return (
    <>
      <Button icon={Check} size="sm" disabled={respond.isPending} onClick={() => respond.mutate({ calloutId, answer: 'accept' })}>
        I'll take it
      </Button>
      {callout.myAnswer?.answer !== 'decline' && (
        <Button icon={X} variant="secondary" size="sm" disabled={respond.isPending} onClick={() => respond.mutate({ calloutId, answer: 'decline' })}>
          Can't make it
        </Button>
      )}
      {failure && (
        <span role="alert" className="text-[11px] text-coral">
          {failure}
        </span>
      )}
    </>
  )
}
